import React, { useCallback, useEffect, useState } from 'react';
import AppLayout from '../../../component/appLayout';
import {
  Group,
  Content,
  ColumnContent,
  PaymentBlock,
  Title1,
  Text,
  Icon,
  Header,
  PersonalText,
  TextBlocks,
  Row,
  ConnectBtn,
  Text3,
  Container
} from '../styles';
import Button from '../../../component/button';
import { STRIPE_URL } from '../../../global/environment';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import { disconnectStripe, getCompanySettingIntegration } from '../../../services/settingServices';
import { captureException } from '../../../services/logService';
import { isEmpty } from '../../../helpers/common';
import { useMobileDevice } from '../../../global/useMobile';
import { BUTTON_TYPE } from '../../../global/constants';
import ResponsiveHeader from '../../../component/responsiveHeader';
import BottomSubNavigation from '../../../component/bottomNavigation/subNavigation';
import { rootStore } from '../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';

interface StripeCheck {
  stripe_connected?: boolean;
  company_name?: string;
}

const Payment: React.FC = () => {
  // States
  const [stripeCheck, setStripeCheck] = useState<StripeCheck>();
  const [loading, setLoading] = useState<boolean>(false);
  // Mobx store variables
  const { workspaceStore } = rootStore;
  const { currentWorkspace: workspace } = workspaceStore;
  const workspace_id = workspace?.id;
  // Other variables
  const isMobile = useMobileDevice();

  const checkStripeConnected = useCallback(async () => {
    if (!isEmpty(workspace_id)) {
      const result = await getCompanySettingIntegration(workspace_id);
      setStripeCheck(result);
    }
  }, [workspace_id]);

  useEffect(() => {
    checkStripeConnected();
  }, [checkStripeConnected]);

  const onClickStripe = useCallback(() => {
    try {
      setLoading(true);
      const newTab = window.open(STRIPE_URL, '_blank');
      if (newTab) {
        newTab.focus();
      } else {
        alert('Please allow pop-ups for this site to open in a new tab.');
      }
    } catch (e) {
      captureException(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const onDisconnectStripe = useCallback(async () => {
    try {
      setLoading(true);
      const result = await disconnectStripe();
      if (result) {
        checkStripeConnected();
      }
    } catch (e) {
      captureException(e);
    } finally {
      setLoading(false);
    }
  }, [checkStripeConnected]);

  return (
    <>
      <AppLayout>
        <Container className='desktop-content'>
          <Group>
            <Content>
              {!isMobile ? (
                <Header>
                  <PersonalText>Online Payment</PersonalText>
                </Header>
              ) : (
                <ResponsiveHeader title='Online Payment' />
              )}
              <ColumnContent>
                <PaymentBlock>
                  <ColumnContent>
                    <Title1> Set up Online Payment </Title1>
                    <TextBlocks>
                      <Text>
                        Get paid faster by letting your clients pay your invocies online. You can link an existing
                        Stripe or Paypal account, or start a new one.
                      </Text>
                      <Text3 href='https://help.teamcamp.app/en/articles/9206920-setup-stripe-and-payment'>
                        Learn more
                      </Text3>
                    </TextBlocks>
                  </ColumnContent>
                </PaymentBlock>
                <PaymentBlock>
                  <ColumnContent>
                    <Row>
                      <Icon>
                        <SVGIcon name='stripeLogo' width='96' height='40' viewBox='0 0 96 40' className='fill-color' />
                      </Icon>
                      {stripeCheck?.stripe_connected && <ConnectBtn>Connected</ConnectBtn>}
                    </Row>

                    {stripeCheck?.stripe_connected ? (
                      <Text>
                        You’re currently accepting <span>credit card payments</span> through the Stripe account{' '}
                        <span>{stripeCheck?.company_name || ''}</span>
                      </Text>
                    ) : (
                      <Text>
                        Allows your clients to pay your invoices with their credit card. Stripe fees will apply.
                      </Text>
                    )}
                  </ColumnContent>
                  {!stripeCheck?.stripe_connected ? (
                    <Button
                      buttonType='button'
                      variant={BUTTON_TYPE.PRIMARY}
                      title='Connect with Stripe'
                      isLoading={loading}
                      onClick={onClickStripe}
                    />
                  ) : (
                    <Button
                      buttonType='button'
                      variant={BUTTON_TYPE.SECONDARY}
                      title='Disconnect Account'
                      isLoading={loading}
                      onClick={onDisconnectStripe}
                    />
                  )}
                </PaymentBlock>
              </ColumnContent>
            </Content>
          </Group>
        </Container>
      </AppLayout>
      <BottomSubNavigation />
    </>
  );
};

export default observer(Payment);
